import { Trans, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { allocateAsset, equipmentQueryOptions } from "@/api/Equipment";
import {
  equipmentAssetStatusSchema,
  operationalStatusDictionary,
  operationalStatusSchema,
} from "@medoma/models/equipment";
import Form from "@/components/Form/Form";
import Select from "@medoma/components/Select/Select";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import styles from "./AllocateEquipment.module.scss";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import NoResults from "@/components/NoResults/NoResults";

const PageWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();

  return (
    <div className={styles.allocateEquipment}>
      <div className={styles.pageHeader}>
        <Heading level="h1" size="h2">
          <Trans>Tilldela ny utrustning</Trans>
        </Heading>
        <CloseButton close={() => navigate("..")} />
      </div>
      {children}
    </div>
  );
};

const AllocateEquipment = () => {
  const navigate = useNavigate();
  const { t } = useLingui();
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const {
    data: equipment,
    isPending,
    isError,
    error,
  } = useQuery(equipmentQueryOptions());

  const {
    formState: { errors },
    handleSubmit,
    register,
    resetField,
    watch,
  } = useForm({
    defaultValues: {
      categoryId: "",
      assetId: "",
    },
  });

  const { mutate, isPending: isAllocating } = useMutation({
    mutationFn: allocateAsset,
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att tilldela utrustningen.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["equipment"] });
      void navigate("..");
    },
  });

  if (isPending) {
    return (
      <PageWrapper>
        <Loading message={t`Hämtar tillgänglig utrustning`} />
      </PageWrapper>
    );
  }

  if (isError) {
    return (
      <PageWrapper>
        <ErrorMessage
          message={`${t`Kunde inte hämta tillgänglig utrustning.`} ${deducedError(error)}`}
        />
      </PageWrapper>
    );
  }

  if (equipment.length === 0) {
    return (
      <PageWrapper>
        <NoResults
          message={t`Den här enheten har ingen utrustning att tilldela.`}
        />
      </PageWrapper>
    );
  }

  const validateAndSubmit = handleSubmit((formData) => {
    mutate({
      assetId: formData.assetId,
      careEpisodeId: patientId,
    });
  });

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const selectedCategoryId = watch("categoryId");

  const categories = equipment.map((category) => {
    const allocatableAssets = category.assets.filter(
      (asset) =>
        asset.status === equipmentAssetStatusSchema.enum.available &&
        asset.operationalStatus === operationalStatusSchema.enum.active,
    );
    const inactiveAssets = category.assets.filter(
      (asset) =>
        asset.operationalStatus === operationalStatusSchema.enum.inactive,
    );
    return { ...category, allocatableAssets, inactiveAssets };
  });

  const selectedCategory = categories.find(
    (category) => category.id === selectedCategoryId,
  );

  return (
    <PageWrapper>
      <div className={styles.formWrapper}>
        <Form onSubmit={validateAndSubmit}>
          <Form.Row>
            <Select
              label={t`Kategori`}
              errorMessage={errors.categoryId?.message}
              {...register("categoryId", {
                required: {
                  value: true,
                  message: t`Välj en kategori`,
                },
                onChange: () => {
                  resetField("assetId");
                },
              })}
            >
              <option value="" disabled>
                {t`Välj kategori`}
              </option>
              {categories.map((category) => {
                const hasAvailableAssets =
                  category.allocatableAssets.length > 0;
                return (
                  <option
                    value={category.id}
                    key={category.id}
                    disabled={!hasAvailableAssets}
                  >
                    {hasAvailableAssets
                      ? category.name
                      : `${category.name} ${t`(ingen utrustning tillgänglig)`}`}
                  </option>
                );
              })}
            </Select>
          </Form.Row>
          {selectedCategory ? (
            <Form.Row>
              <Select
                label={t`Tillgänglig utrustning`}
                errorMessage={errors.assetId?.message}
                {...register("assetId", {
                  required: {
                    value: true,
                    message: t`Välj utrustning`,
                  },
                })}
              >
                <option value="" disabled>
                  {t`Välj utrustning`}
                </option>
                {selectedCategory.allocatableAssets.map((asset) => (
                  <option value={asset.id} key={asset.id}>
                    {asset.comment
                      ? `${asset.name} (${asset.comment})`
                      : asset.name}
                  </option>
                ))}
                {selectedCategory.inactiveAssets.map((asset) => (
                  <option value={asset.id} key={asset.id} disabled>
                    {asset.comment
                      ? `${asset.name} (${asset.comment}) (${t(operationalStatusDictionary.inactive)})`
                      : `${asset.name} (${t(operationalStatusDictionary.inactive)})`}
                  </option>
                ))}
              </Select>
            </Form.Row>
          ) : undefined}

          {errors.root?.server?.message ? (
            <ErrorMessage message={errors.root.server.message} />
          ) : null}

          <div className={styles.buttons}>
            <OutlinedButton onClick={() => navigate("..")}>
              <Trans>Avbryt</Trans>
            </OutlinedButton>
            <FilledButton type="submit" disabled={isAllocating}>
              <Trans>Tilldela</Trans>
            </FilledButton>
          </div>
        </Form>
      </div>
    </PageWrapper>
  );
};

export default AllocateEquipment;
